import CopyText from "./copy_text_controller";
import { get } from "@rails/request.js"

export default class extends CopyText {
  static targets = [
    "radioButton", "configSection", "selectedType", "embedCode", "typeCard",
    "previewContainer", "previewPlaceholder", "directLink",
    // Event Card (DS)
    "cardSettings", "cardPreview", "cardCtaLabel", "cardTheme", "cardThemeSeg",
    "cardAccent", "cardAccentSeg", "cardBrandHex", "cardBrandHexWrap",
    "showCardImage", "showCardLocation", "showCardPrice",
    // Register Button (DS)
    "buttonSettings", "buttonPreview", "buttonLabel", "buttonSize", "buttonSizeSeg",
    "buttonShape", "buttonShapeSeg", "buttonStyle", "buttonStyleSeg",
    "buttonAccent", "buttonBrandHex", "buttonBrandHexWrap",
    // Ticket List (DS)
    "ticketListSettings", "ticketListPreview", "ticketListTitle", "ticketListSubtitle",
    "ticketListShowImage", "ticketListShowLocation", "ticketListTheme", "ticketListThemeSeg",
    "ticketListLayout", "ticketListLayoutSeg", "ticketListMaxQty"
  ]

  static values = {
    eventId: String,
    eventSlug: String,
    copiedSuccess: String,
  }

  connect() {
    this.initializeDefaults()
    // Defer the themed-select init to the next animation frame so it runs AFTER
    // the global turbo:load select2 init (custom.js) — see initThemedSelects for
    // the full root-cause note. Running last lets us reset whatever the global
    // init created and own these pickers cleanly.
    this._themedSelectFrame = requestAnimationFrame(() => this.initThemedSelects())
  }

  disconnect() {
    if (this._themedSelectFrame) cancelAnimationFrame(this._themedSelectFrame)
  }

  initThemedSelects() {
    // Themed select2 pickers (no native browser-blue <select>). Scoped via the
    // .ew-select-wrap (.select2-search-off) wrapper + .ew-themed-dropdown class.
    //
    // ROOT CAUSE of the blank "Max quantity" field: these <select>s carry the
    // .ew-themed-select class and were ALSO given the bare .select2 class, so the
    // global turbo:load init (custom.js: `$('.select2').each(...)`) initialized
    // them in addition to this scoped init. Worse, once select2 renders, the
    // generated `.select2-container` SPAN itself carries a `.select2` class — so a
    // later `$('.select2')` pass re-runs `.select2()` on that container span and
    // stacks a SECOND, empty/broken picker on top of the working one (the field
    // then shows blank / "No results found" / a stray search box).
    //
    // Fix: this method runs once per connect, deferred to AFTER the global init
    // (requestAnimationFrame), and for each themed select it (1) destroys any
    // existing instance, (2) hard-removes every rendered `.select2-container`
    // span in the wrapper (select2's own destroy can orphan one), (3) clears
    // select2's bookkeeping attrs/classes back to a pristine <select>, then
    // (4) re-inits ONCE with the configurator's themed opts.
    if (typeof window.initSelect2 === "function") {
      this.element.querySelectorAll(".ew-themed-select").forEach((el) => {
        el.classList.remove("select2")
        if (window.jQuery) {
          const $el = window.jQuery(el)
          if ($el.data("select2")) $el.select2("destroy")
        }
        const wrap = el.closest(".select2-search-off") || el.parentElement
        if (wrap) wrap.querySelectorAll(".select2-container").forEach((c) => c.remove())
        el.classList.remove("select2-hidden-accessible")
        el.removeAttribute("aria-hidden")
        el.removeAttribute("tabindex")
        el.removeAttribute("data-select2-id")
        el.querySelectorAll("[data-select2-id]").forEach((o) => o.removeAttribute("data-select2-id"))
        const opts = {
          minimumResultsForSearch: Infinity,
          allowClear: false,
          dropdownCssClass: "ew-themed-dropdown",
          width: "100%"
        }
        // CTA-label select ONLY: render an icon + label per option/selection,
        // reusing the SAME lucide SVG set drawn on the rendered CTA button so the
        // dropdown and button stay in sync. select2 injects its own nodes, so we
        // emit inline SVG markup (lucide.createIcons() won't re-run on them).
        if (this.hasCardCtaLabelTarget && el === this.cardCtaLabelTarget) {
          opts.templateResult = this.ctaTemplate.bind(this)
          opts.templateSelection = this.ctaTemplate.bind(this)
          opts.escapeMarkup = (m) => m
        }
        window.initSelect2(el, el.closest(".select2-search-off") || null, opts)

        // select2 updates the underlying <select>'s value but fires its OWN
        // jQuery `change`/`select2:select` event — which Stimulus's native
        // `data-action="change->..."` listeners do NOT catch. So picking a new
        // Label (or card CTA) left the preview stale ("Get tickets" while the
        // select read "RSVP"). Re-emit a NATIVE change event when select2 fires
        // so the existing data-action handlers run.
        if (window.jQuery) {
          window.jQuery(el).on("select2:select select2:unselect change", () => {
            el.dispatchEvent(new Event("change", { bubbles: true }))
          })
        }

        // select2 stamps a bare `.select2` class onto the container span it just
        // rendered. The global turbo:load init (custom.js) runs `$('.select2')
        // .each(...)` — which fires AFTER us (observed ~860ms later) — and would
        // match THAT container span, re-running `.select2()` on it and stacking a
        // second, empty/broken picker. Strip `.select2` from our rendered
        // container(s) so the global pass can no longer find them.
        if (wrap) wrap.querySelectorAll(".select2-container.select2").forEach((c) => c.classList.remove("select2"))
      })
    }
  }

  // Inline lucide SVG paths — must match app/views/widgets/event_card.html.erb.
  ctaIconSvg(name) {
    const paths = {
      "ticket": "<path d='M2 9a3 3 0 0 0 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z'/>",
      "user-plus": "<path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2'/><circle cx='9' cy='7' r='4'/><path d='M19 8v6M22 11h-6'/>",
      "external-link": "<path d='M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6'/>"
    }
    const p = paths[name] || paths["ticket"]
    return `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;vertical-align:middle">${p}</svg>`
  }

  ctaTemplate(state) {
    if (!state || !state.id) return state ? state.text : ""
    const opt = state.element
    const icon = opt && opt.dataset ? opt.dataset.icon : null
    if (!icon) return state.text
    return `<span style="display:inline-flex;align-items:center;gap:8px">${this.ctaIconSvg(icon)}<span>${state.text}</span></span>`
  }

  initializeDefaults() {
    this.currentWidgetType = 'event-card'
    if (this.hasSelectedTypeTarget) {
      this.selectedTypeTarget.innerHTML = '<p class="widget-type-tag">Widget Type: <span>Event Card</span></p>'
    }
    this.hideAllSettings()
    if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'block'
    this.updateCardPreview()
  }

  hideAllSettings() {
    if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'none'
    if (this.hasButtonSettingsTarget) this.buttonSettingsTarget.style.display = 'none'
    if (this.hasTicketListSettingsTarget) this.ticketListSettingsTarget.style.display = 'none'
  }

  selectWidget(event) {
    const radio = event.currentTarget
    const widgetType = radio.value
    if (!radio.checked) return

    this.currentWidgetType = widgetType
    this.typeCardTargets.forEach(card => card.classList.remove('active'))
    radio.closest('.type-selection-card').classList.add('active')
    if (this.hasConfigSectionTarget) this.configSectionTarget.style.display = 'block'

    const typeNames = {
      'event-card': 'Event Card',
      'register-button': 'Register Button',
      'ticket-list': 'Ticket List'
    }
    if (this.hasSelectedTypeTarget) {
      this.selectedTypeTarget.innerHTML = `<p class="widget-type-tag">Widget Type: <span>${typeNames[widgetType]}</span></p>`
    }

    this.hideAllSettings()

    if (widgetType === 'event-card') {
      if (this.hasCardSettingsTarget) this.cardSettingsTarget.style.display = 'block'
      this.updateCardPreview()
    } else if (widgetType === 'register-button') {
      if (this.hasButtonSettingsTarget) this.buttonSettingsTarget.style.display = 'block'
      this.updateButtonPreview()
    } else if (widgetType === 'ticket-list') {
      if (this.hasTicketListSettingsTarget) this.ticketListSettingsTarget.style.display = 'block'
      this.updateTicketListPreview()
    }
  }

  selectCard(event) {
    if (event.target.type !== 'radio') {
      const radio = event.currentTarget.querySelector('input[type="radio"]')
      if (radio) {
        radio.checked = true
        this.selectWidget({ currentTarget: radio })
      }
    }
  }

  // ── Segmented control helper ────────────────────────────────────────────
  setSegment(segTarget, hiddenTarget, value) {
    segTarget.querySelectorAll('.ew-seg-btn').forEach(btn => {
      btn.classList.toggle('is-sel', btn.dataset.value === value)
    })
    hiddenTarget.value = value
  }

  // ── Event Card ──────────────────────────────────────────────────────────
  selectCardTheme(event) {
    this.setSegment(this.cardThemeSegTarget, this.cardThemeTarget, event.currentTarget.dataset.value)
    this.updateCardPreview()
  }

  selectCardAccent(event) {
    const value = event.currentTarget.dataset.value
    this.setSegment(this.cardAccentSegTarget, this.cardAccentTarget, value)
    if (this.hasCardBrandHexWrapTarget) this.cardBrandHexWrapTarget.style.display = value === 'brand' ? 'block' : 'none'
    this.updateCardPreview()
  }

  updateCardPreview() {
    this.showPreview('event-card', this.cardPreviewTarget, this.getCardSettings())
    this.updateEmbedCode('event-card')
  }

  getCardSettings() {
    return {
      card_theme: this.hasCardThemeTarget ? this.cardThemeTarget.value : 'light',
      card_accent: this.hasCardAccentTarget ? this.cardAccentTarget.value : 'stubs',
      card_brand_hex: this.hasCardBrandHexTarget ? this.cardBrandHexTarget.value : '#1A1A1A',
      show_card_image: this.hasShowCardImageTarget ? this.showCardImageTarget.checked : true,
      show_card_location: this.hasShowCardLocationTarget ? this.showCardLocationTarget.checked : true,
      show_card_price: this.hasShowCardPriceTarget ? this.showCardPriceTarget.checked : true,
      card_cta: this.hasCardCtaLabelTarget ? this.cardCtaLabelTarget.value : 'Get tickets'
    }
  }

  // ── Register Button ─────────────────────────────────────────────────────
  selectButtonSize(event) {
    this.setSegment(this.buttonSizeSegTarget, this.buttonSizeTarget, event.currentTarget.dataset.value)
    this.updateButtonPreview()
  }

  selectButtonShape(event) {
    this.setSegment(this.buttonShapeSegTarget, this.buttonShapeTarget, event.currentTarget.dataset.value)
    this.updateButtonPreview()
  }

  selectButtonStyle(event) {
    const value = event.currentTarget.dataset.value
    this.setSegment(this.buttonStyleSegTarget, this.buttonStyleTarget, value)
    // accent follows style: brand → brand hex, otherwise stubs
    if (this.hasButtonAccentTarget) this.buttonAccentTarget.value = value === 'brand' ? 'brand' : 'stubs'
    if (this.hasButtonBrandHexWrapTarget) this.buttonBrandHexWrapTarget.style.display = value === 'brand' ? 'block' : 'none'
    this.updateButtonPreview()
  }

  updateButtonPreview() {
    this.showPreview('register-button', this.buttonPreviewTarget, this.getButtonSettings())
    this.updateEmbedCode('register-button')
  }

  getButtonSettings() {
    return {
      button_label: this.hasButtonLabelTarget ? this.buttonLabelTarget.value : 'Get tickets',
      button_theme: this.hasButtonThemeTarget ? this.buttonThemeTarget.value : 'light',
      button_size: this.hasButtonSizeTarget ? this.buttonSizeTarget.value : 'default',
      button_shape: this.hasButtonShapeTarget ? this.buttonShapeTarget.value : 'rounded',
      button_style: this.hasButtonStyleTarget ? this.buttonStyleTarget.value : 'solid',
      button_accent: this.hasButtonAccentTarget ? this.buttonAccentTarget.value : 'stubs',
      button_brand_hex: this.hasButtonBrandHexTarget ? this.buttonBrandHexTarget.value : '#1A1A1A'
    }
  }

  // ── Ticket List ─────────────────────────────────────────────────────────
  selectTicketListTheme(event) {
    this.setSegment(this.ticketListThemeSegTarget, this.ticketListThemeTarget, event.currentTarget.dataset.value)
    this.updateTicketListPreview()
  }

  selectTicketListLayout(event) {
    this.setSegment(this.ticketListLayoutSegTarget, this.ticketListLayoutTarget, event.currentTarget.dataset.value)
    this.updateTicketListPreview()
  }

  updateTicketListPreview() {
    this.showPreview('ticket-list', this.ticketListPreviewTarget, this.getTicketListSettings())
    this.updateEmbedCode('ticket-list')
  }

  getTicketListSettings() {
    return {
      ticket_list_title: this.hasTicketListTitleTarget ? this.ticketListTitleTarget.value : '',
      ticket_list_subtitle: this.hasTicketListSubtitleTarget ? this.ticketListSubtitleTarget.value : '',
      ticket_list_theme: this.hasTicketListThemeTarget ? this.ticketListThemeTarget.value : 'light',
      ticket_list_layout: this.hasTicketListLayoutTarget ? this.ticketListLayoutTarget.value : 'stacked',
      ticket_list_show_image: this.hasTicketListShowImageTarget ? this.ticketListShowImageTarget.checked : true,
      ticket_list_show_location: this.hasTicketListShowLocationTarget ? this.ticketListShowLocationTarget.checked : true,
      ticket_list_max_qty: this.hasTicketListMaxQtyTarget ? this.ticketListMaxQtyTarget.value : '10'
    }
  }

  // Opens the QR modal for the widget as currently configured. Requests the
  // turbo_stream so the server renders the shared QR modal (same flow as the
  // event / organization QR), passing the live settings so the code encodes the
  // same URL the Direct Link field shows.
  openQrCode() {
    const params = new URLSearchParams({
      widget_type: 'ticket-list',
      ...this.getTicketListSettings()
    })
    const url = `/organizer/event_dashboard/events/${this.eventIdValue}/widgets/qr_code?${params}`

    get(url, { responseKind: 'turbo-stream' }).catch(error => {
      console.error('Error loading QR code:', error)
    })
  }

  // ── Shared preview / embed ──────────────────────────────────────────────
  showPreview(widgetType, previewTarget, settings) {
    // hide all previews + placeholder
    if (this.hasCardPreviewTarget) this.cardPreviewTarget.style.display = 'none'
    if (this.hasButtonPreviewTarget) this.buttonPreviewTarget.style.display = 'none'
    if (this.hasTicketListPreviewTarget) this.ticketListPreviewTarget.style.display = 'none'
    if (this.hasPreviewPlaceholderTarget) this.previewPlaceholderTarget.style.display = 'none'

    previewTarget.style.display = 'block'

    const url = `/organizer/event_dashboard/events/${this.eventIdValue}/widgets/preview`
    get(url, {
      query: { widget_type: widgetType, ...settings },
      headers: { 'X-CSRF-Token': this.getCSRFToken(), 'Accept': 'application/json' }
    }).then(response => {
      if (response.ok) return response.json
      throw new Error('Network response was not ok')
    }).then(data => {
      if (data.status === 'success') previewTarget.innerHTML = data.html
    }).catch(error => {
      console.error('Error loading preview:', error)
      previewTarget.innerHTML = '<div class="alert alert-danger">Error loading preview</div>'
    })
  }

  updateEmbedCode(widgetType) {
    let settings = {}
    if (widgetType === 'event-card') settings = this.getCardSettings()
    else if (widgetType === 'register-button') settings = this.getButtonSettings()
    else if (widgetType === 'ticket-list') settings = this.getTicketListSettings()

    const url = `/organizer/event_dashboard/events/${this.eventIdValue}/widgets/embed_code`
    get(url, {
      query: { widget_type: widgetType, ...settings },
      headers: { 'X-CSRF-Token': this.getCSRFToken(), 'Accept': 'application/json' }
    }).then(response => {
      if (response.ok) return response.json
      throw new Error('Network response was not ok')
    }).then(data => {
      if (data.status !== 'success') return
      this.embedCodeTarget.textContent = data.embed_code
      if (this.hasDirectLinkTarget) this.directLinkTarget.value = data.direct_url || ''
    }).catch(error => {
      console.error('Error generating embed code:', error)
      this.embedCodeTarget.textContent = '<!-- Error generating embed code -->'
      if (this.hasDirectLinkTarget) this.directLinkTarget.value = ''
    })
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : null
  }

  selectDirectLink() {
    if (this.hasDirectLinkTarget) this.directLinkTarget.select()
  }

  // Both copy actions go through CopyText#copyToClipboard, which handles the
  // insecure-origin case (http://lvh.me has no navigator.clipboard) and only
  // reports success when the text actually reached the clipboard. Each handler
  // flashes the button that was clicked — keying off the DOM by action name
  // made either click light up both buttons.
  async copyDirectLink(event) {
    if (!this.hasDirectLinkTarget) return
    const url = this.directLinkTarget.value
    if (!url) return
    if (await this.copyToClipboard(url)) this.flashCopied(event.currentTarget)
  }

  async copyEmbedCode(event) {
    const embedCode = this.embedCodeTarget.textContent
    if (!embedCode) return
    if (await this.copyToClipboard(embedCode)) this.flashCopied(event.currentTarget)
  }

  flashCopied(button) {
    if (!button) return
    const originalText = button.textContent
    button.textContent = this.copiedSuccessValue || 'Copied!'
    button.classList.add('btn-success')
    button.classList.remove('btn-primary')
    setTimeout(() => {
      button.textContent = originalText
      button.classList.remove('btn-success')
      button.classList.add('btn-primary')
    }, 2000)
  }
}
